import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import { useClienteCombos, type MovimentoCombo } from './useClienteCombos';
import '../contracts/Combos_SalvarInput';
import '../contracts/ComboRelatorios_UnidadeInput';
import '../contracts/Combos_ListaResponse';
import '../contracts/Combos_GetResponse';
import '../contracts/Combos_SalvarResponse';
import '../contracts/ComboRelatorios_UnidadeResponse';
import type { Combos_ComboRow } from '../contracts/Combos_ComboRow';
import type { Combos_SalvarInput } from '../contracts/Combos_SalvarInput';
import type { ComboRelatorios_UnidadeInput } from '../contracts/ComboRelatorios_UnidadeInput';
import type { ComboRelatorios_UnidadeComboRow } from '../contracts/ComboRelatorios_UnidadeComboRow';
import type {
  ComboRelatorios_UnidadeResponseDados,
} from '../contracts/ComboRelatorios_UnidadeResponse';
import type { ComboRelatorios_UnidadeTotaisRow } from '../contracts/ComboRelatorios_UnidadeTotaisRow';

/**
 * O catálogo GLOBAL de combos pré-pagos (NS 2026-0090, só o Full escreve) e o
 * relatório por unidade. Preço e ativação por unidade: useSOComboPrices.
 *
 * Fronteira do relatório: o wire fala CENTAVOS, a tela fala unidade da moeda.
 */
export interface RelatorioCombosUnidade {
  combos: ComboRelatorios_UnidadeComboRow[];
  totais: ComboRelatorios_UnidadeTotaisRow;
  movimentos: MovimentoCombo[];
}

const emUnidades = <T extends { valor_vendido?: number; valor_cancelado?: number }>(linha: T): T => ({
  ...linha,
  valor_vendido: (linha.valor_vendido ?? 0) / 100,
  valor_cancelado: (linha.valor_cancelado ?? 0) / 100,
});

export function useSOCombos() {
  const { movimentoParaTela } = useClienteCombos();
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const basePath = '/index.php/so/combos';

  const fetchAll = async (): Promise<Combos_ComboRow[]> => {
    log('useSOCombos', 'fetchAll', 'Fetching catalog');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'Combos_ListaResponse' });
      if (response.success) {
        return (response.dados || []) as Combos_ComboRow[];
      }
      throw new Error(response.error || 'Erro ao buscar os combos');
    } catch (err) {
      logError('useSOCombos', 'fetchAll', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getById = async (id: number): Promise<Combos_ComboRow> => {
    log('useSOCombos', 'getById', 'Fetching combo', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/get/${id}`, { responseContract: 'Combos_GetResponse' });
      if (response.success) {
        return response.dados as Combos_ComboRow;
      }
      throw new Error(response.error || 'Erro ao buscar o combo');
    } catch (err) {
      logError('useSOCombos', 'getById', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: Combos_SalvarInput) => {
    log('useSOCombos', 'save', 'Saving combo', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('Combos_SalvarInput', data);
      const response = await httpService.post(`${basePath}/salvar`, data, { responseContract: 'Combos_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar o combo');
    } catch (err) {
      logError('useSOCombos', 'save', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchRelatorioUnidade = async (input: ComboRelatorios_UnidadeInput): Promise<RelatorioCombosUnidade> => {
    log('useSOCombos', 'fetchRelatorioUnidade', 'Fetching report', input);
    isLoading.value = true;
    error.value = null;
    try {
      validate('ComboRelatorios_UnidadeInput', input);
      const qs = new URLSearchParams({
        franquia_id: String(input.franquia_id),
        data_inicio: input.data_inicio,
        data_fim: input.data_fim,
      }).toString();
      const response = await httpService.get(`/index.php/so/combo_relatorios/unidade?${qs}`, { responseContract: 'ComboRelatorios_UnidadeResponse' });
      if (!response.success) {
        throw new Error(response.error || 'Erro ao buscar o relatório de combos');
      }
      const dados = response.dados as ComboRelatorios_UnidadeResponseDados;
      return {
        combos: dados.combos.map(emUnidades),
        totais: emUnidades(dados.totais),
        movimentos: dados.movimentos.map(movimentoParaTela),
      };
    } catch (err) {
      logError('useSOCombos', 'fetchRelatorioUnidade', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchAll,
    getById,
    save,
    fetchRelatorioUnidade,
  };
}
